import React, { Component, Fragment } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import 'moment/locale/ru';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import upperFirst from 'lodash/upperFirst';

import {
  getUserFullName,
  getMessageSnippet,
  extractPreviewsFromMessage,
} from '../../utils/utilsChat';

import {
  inflect,
  flashElement,
  scrollToElement,
  isWithinElement,
  isMobileSizeScreen,
  exposeComponentToGlobalScope,
} from '../../utils/utilsGlobal';

import services, { events } from '../../services/services';
import { unlinkMessageFromIssue } from '../../services/apiIssues';
import {
  ChatMessagesAttachments,
  FullScreenPreviews,
  FormattedMarkdown,
  IssueLinkingPopup,
  Tooltip,
  Avatar,
  Modal,
} from '../../components';

import styles from './ChatMessages.scss';
import uiStyles from '../../styles/ui.scss';
import whatsappTemplateTooltipImage from '../../../public/assets/images/whatsapp-template-tooltip.png';


export class ChatMessages extends Component {

  state = {
    processedMessages: [],
    fullScreenPreviews: [],
    currentMessageActionsMenuId: null,
    areMessagedRendered: false,
    issuePreview: null,
    linkingToIssueMessageId: null,
    isUnlinkingIssueFromMessage: false,
    // Лог оригинальных сообщений, которые мы выделяем при поиске
    originalMessages: {}
  };

  messageRefs = {};
  fullScreenPreviewer = React.createRef();

  componentDidMount(){
    const { messages } = this.props;
    moment.locale('ru');
    this.processMessages(messages);

    services.pushNotifications.on(events.PUSH_NOTIFICATIONS_FORCE_SCROLL_TO_MESSAGE, messageId => {
      this.scrollToMessage(messageId, flashElement);
    });
    document.body.addEventListener('click', this.onBodyClick);
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(prevProps){
    const { messages, searchMessagesIds } = this.props;
    // TODO; максимально сыро, потом написать нормальные условия
    if (messages !== prevProps.messages) {
      if (searchMessagesIds) {
        this.markedSearchText();
      } else {
        this.processMessages(messages);
      }
    }
    if (searchMessagesIds !== prevProps.searchMessagesIds) {
      if (searchMessagesIds.length) {
        this.markedSearchText();
      } else {
        this.setState({ originalMessages: {} });
        this.processMessages(messages);
      }
    }
  }

  componentWillUnmount(){
    document.body.removeEventListener('click', this.onBodyClick);
  }

  onBodyClick = (e) => {
    if (!isWithinElement(e.target, styles.messageActionsIcon)) {
      this.setState({ currentMessageActionsMenuId: null });
    }
  };

  processMessages = (messages) => {
    const firstUnreadMessage = find(messages, { isUnread: true }) || {};
    let fullScreenPreviews = [];

    const processedMessages = cloneDeep(messages).map((message, i) => {
      const previousMessage = messages[i - 1] || {};
      const sameDate = moment(message.timestamp).isSame(previousMessage.timestamp, 'day');

      if (!message.isSystem) {
        const hasMentions = message.mentions.length;
        const {
          sender: previousSender = {},
          isSystem: previousIsSystem,
          isDeleted: previousIsDeleted,
        } = previousMessage;

        const sameSender = previousSender.id === message.sender.id;
        if (sameDate && sameSender && !hasMentions && !previousIsSystem && !previousIsDeleted) {
          message.hideSender = true;
        }
      }

      message.showDateLabel = !sameDate;
      message.showUnreadMessagesLabel = firstUnreadMessage.id === message.id;

      if (!message.isDeleted) {
        fullScreenPreviews = fullScreenPreviews.concat(extractPreviewsFromMessage(message));
      }
      return message;
    });

    this.setState({
      processedMessages,
      fullScreenPreviews,
    });
  };

  scrollToMessage = (messageId, callback, options) => {
    const messageElement = this.messageRefs[messageId];
    scrollToElement(messageElement, () => {
      callback && callback(messageElement);
    }, options);
  };

  createMessageRef = (messageElement, message) => {
    this.messageRefs[message.id] = messageElement;
  };

  toggleMessageActionsMenu = (message) => {
    requestAnimationFrame(() => {
      const { currentMessageActionsMenuId } = this.state;
      this.setState({
        currentMessageActionsMenuId: currentMessageActionsMenuId ? null : message.id,
      });
    });
  };

  deleteMessage = (message) => {
    const confirmationText = 'Вы действительно хотите удалить сообщение?';
    if (confirm(confirmationText)) {
      services.messageSubscription.deleteMessage(message.id);
    }
  };

  renderSubmissionErrorMessage = (message) => {
    const { isEditing, code } = message.submissionError || {};

    const defaultMessage = (
      <div className={styles.submissionError}>
        {isEditing ? 'Ошибка редактирования' : 'Ошибка отправки'}
        <span className={styles.submissionErrorAction}
          onClick={() => isEditing ? services.messageSubscription.retryEditMessage(message) : services.messageSubscription.retrySendMessage(message)}>
          Еще раз <i className={cn('icon-undo', styles.submissionErrorRepeat)}/>
        </span>
      </div>
    );
    const badConnectionMessage = (
      <div className={styles.submissionError}>
        {isEditing ? 'Не отредактировано' : 'Не отправлено'}
        <span className={styles.submissionErrorAction}
          onClick={() => isEditing ? services.messageSubscription.retryEditMessage(message) : services.messageSubscription.retrySendMessage(message)}>
          Еще раз <i className={cn('icon-undo', styles.submissionErrorRepeat)}/>
        </span>
      </div>
    );
    const unsupportedFileTypeMessage = (
      <div className={styles.submissionError}>
        {isEditing ? 'Не отредактировано' : 'Не отправлено'}: вложения такого типа не поддерживаются
      </div>
    );
    const tooLargeFileMessage = (
      <div className={styles.submissionError}>
        {isEditing ? 'Не отредактировано' : 'Не отправлено'}: максимальный размер файлов — 30Мб
      </div>
    );
    const messageByErrorCode = {
      '415': unsupportedFileTypeMessage,
      '413': tooLargeFileMessage,
      '503': badConnectionMessage,
    };
    return messageByErrorCode[code] || defaultMessage;
  };

  renderQueuedMessage = (message) => {
    if (message.queued === 'WHATSAPP_FREE_INTERVAL') {
      const tooltip = (
        <Fragment>
          Это сообщение отправлено спустя 24 часа<br/>
          после ответа от клиента, поэтому, по<br/>
          <a href="https://developers.facebook.com/docs/whatsapp/api/messages#cutoffcontrol" target="_blank" rel="noreferrer noopener"> правилам WhatsApp
          </a>, будет доставлено лишь<br/>
          когда клиент снова напишет. Но мы отправили<br/>
          клиенту шаблонное уведомление.<br/>
          <img src={whatsappTemplateTooltipImage}/>
        </Fragment>
      );
      return (
        <Tooltip className={styles.queuedMessageTooltip} title={tooltip} align="left">
          <div className={styles.queuedMessage}>
            <i className={cn(styles.queuedMessageIcon, 'icon-delay')}/>
            Ожидает отправки
          </div>
        </Tooltip>
      );
    }
  };

  renderTimestamp = (timestamp) => {
    return (
      <Tooltip className={styles.timestampTooltip} align="right"
        title={moment(timestamp).format('D MMMM YYYY, H:mm:ss').replace(',', ',\n')}>
        <div className={styles.timestamp}>
          {moment(timestamp).format('H:mm')}
        </div>
      </Tooltip>
    );
  };

  onUnlinkIssueClick = ({ issue, message }) => {
    this.setState({ isUnlinkingIssueFromMessage: true });
    unlinkMessageFromIssue(issue.id, message.id)
      .then(() => {
        this.setState({ issuePreview: null });
      })
      .finally(() => {
        this.setState({ isUnlinkingIssueFromMessage: false });
      });
  };

  getSystemMessageIconByType = (type) => {
    const dict = {
      ReminderSetMessage: 'icon-system-reminder',
      ReminderFiredMessage: 'icon-system-reminder',
      CaseNotClosedNotifyMessage: 'icon-system-reminder',
      ScreenshotRequestedMessage: 'icon-system-screenshot',
      WebhookSetMessage: 'icon-system-jira',
      WebhookUpdatedMessage: 'icon-system-jira',
      CaseClosedMessage: 'icon-system-case-closed',
      OperatorAssignedMessage: 'icon-system-operator-assigned',
    };
    return dict[type] || uiStyles.hidden;
  };
  
  /** SEARCH **/
  /**
   * Поиск сообщения в загруженном списке
   */
  markedSearchText() {
    const { searchText } = this.props;
    const messages = cloneDeep(this.props.messages)
    let { originalMessages } = this.state;
    
    messages.forEach(el => {
      if (!this.props.searchMessagesIds.includes(el.id)) {
        return;
      }
      
      // сохраняем старое значение сообщение, чтобы вернуть его при новом поиске.
      if (Object.hasOwnProperty.call(originalMessages, el.id)) {
        el.text = originalMessages[el.id];
        delete el.isMarked;
        delete originalMessages[el.id];
      }
      if (!el.isMarked) {
        originalMessages[el.id] = el.text;
        el.isMarked = true;
        el.text = el.text.replace(new RegExp(searchText, "gi"), (match) => `★${match}★`);
      }
    })
    this.setState({ originalMessages });
    this.processMessages(messages);
  }

  render(){
    const {
      currentMessageActionsMenuId,
      fullScreenPreviews,
      processedMessages,
      issuePreview,
      linkingToIssueMessageId,
      isUnlinkingIssueFromMessage,
    } = this.state;

    const {
      className,
      onReplyTo,
      onStartEditingMessage,
      onToggleFullScreenPreview,
      currentlyTypingUsers,
      editingOriginalMessage,
      selectMessageId,
      showScrollButton
    } = this.props;

    const unreadMessageCount = processedMessages.filter(message => message.isUnread).length;

    return (

      <div className={cn({
        [className]: true,
        [styles.hasUnreadMessages]: services.messageSubscription.reachedBeginningOfMessageHistory,
        [styles.isEditingMode]: editingOriginalMessage,
        [styles.messagesWrapper]: true
      })}>

        {processedMessages.map(message => (
          <Fragment key={message.id}>

            {message.showUnreadMessagesLabel && (
              <div className={styles.unreadMessagesLabel}>
                {inflect(unreadMessageCount, ['новое сообщение', 'новых сообщения', 'новых сообщений'])}
              </div>
            )}

            {message.showDateLabel && (
              <time dateTime={message.timestamp} className={styles.dateLabel}>
                {upperFirst(moment(message.timestamp).format('ddd, D MMMM'))}
              </time>
            )}

            {/* System message */}
            {message.isSystem && (
              <div className={cn({
                [styles.message]: true,
                [styles.messageSystem]: true,
                [styles.messageSystemHighlighted]: message.systemData.isHighlightedAttention,
                [styles.messageSystemCaseClosed]: message.systemData.isHighlightedSuccess,
                [styles.messageEditing]: editingOriginalMessage?.id === message.id,
              })}
                ref={element => this.createMessageRef(element, message)}>

                <div className={styles.messageContent}>
                  <div className={styles.messageTopRight}>
                    {this.renderTimestamp(message.timestamp)}
                  </div>
                  <i className={cn(this.getSystemMessageIconByType(message.systemData.type), styles.systemIcon)}/>
                  {message.systemData.textFormatted.map((chunk, i) => (
                    <span key={i} className={cn({
                      [styles.systemText]: true,
                      [styles.systemTextPrimary]: chunk.isPrimary,
                    })}>
                      {Boolean(chunk.url) && (
                        <a href={chunk.url} target="_blank">{chunk.text}</a>
                      )}
                      {!Boolean(chunk.url) && chunk.text}
                    </span>
                  ))}
                </div>
              </div>
            )}


            {/* Deleted user message */}
            {message.isDeleted && (
              <div ref={element => this.createMessageRef(element, message)}
                className={cn({
                [styles.message]: true,
                [styles.messageDeleted]: true,
                [styles.messageSystem]: true,
                [styles.messageEditing]: editingOriginalMessage?.id === message.id,
              })}>
                <div className={styles.messageContent}>
                  <div className={styles.messageTopRight}>
                    {this.renderTimestamp(message.timestamp)}
                  </div>
                  <b>Сообщение удалено</b> <span>{getUserFullName(message.sender)}</span>
                </div>
              </div>
            )}


            {/* Client's or operator's message */}
            {!message.isDeleted && !message.isSystem && (
              <div id={message.id} className={cn({
                [styles.message]: true,
                [styles.messageSelected]: message.id === selectMessageId ,
                [styles.messageCaseOpen]: message.isCaseOpen,
                [styles.messageHideSender]: message.hideSender,
                [styles.messageUnreadIndicator]: message.isUnread && message.hideSender,
                [styles.messageEditing]: editingOriginalMessage?.id === message.id,
              })}
                ref={element => this.createMessageRef(element, message)}>
                {!message.hideSender && (
                  <Avatar className={cn({
                    [styles.avatar]: true,
                    [styles.avatarUnreadIndicator]: message.isUnread,
                  })}
                    user={message.sender}/>
                )}

                <div className={styles.messageContent}>
                  {!message.hideSender && (
                    <div className={styles.author}>
                      <b>{getUserFullName(message.sender)}</b>
                      {Boolean(message.mentions.length) && (
                        <Fragment>
                          &nbsp;→ @&nbsp;
                          {message.mentions.map(mention => {
                            return mention.value === 'ALL' ? 'Все' : getUserFullName(mention.client, '\u00A0');
                          }).join(', ')}
                        </Fragment>
                      )}
                    </div>
                  )}

                  <div className={styles.messageTopRight}>
                    {!message.submissionError && (
                      <Tooltip title="Ответить">
                        <i className={cn(styles.replyButton, 'icon-reply-left')} onClick={() => onReplyTo(message)}/>
                      </Tooltip>
                    )}
                    {this.renderTimestamp(message.timestamp)}
                    {(message.isEditable || message.isLinkableToIssue) && (
                      <div className={styles.messageActions}>
                        <i className={cn(styles.messageActionsIcon, 'icon-dots-menu')}
                          onClick={() => this.toggleMessageActionsMenu(message)}
                          onContextMenu={(e) => {
                            this.toggleMessageActionsMenu(message);
                            e.preventDefault();
                          }}/>

                        <ul className={cn({
                          [styles.messageActionsMenu]: true,
                          [styles.messageActionsMenuOpen]: currentMessageActionsMenuId === message.id,
                        })}>
                          {message.isLinkableToIssue && (
                            <li className={styles.messageActionsItem} onClick={() => this.setState({ linkingToIssueMessageId: message.id })}>
                              Связать с проблемой
                            </li>
                          )}
                          {message.isEditable && (
                            <Fragment>
                              <li className={styles.messageActionsItem} onClick={() => onStartEditingMessage(message)}>
                                Редактировать
                              </li>
                              <li className={styles.messageActionsItem} onClick={() => this.deleteMessage(message)}>
                                Удалить
                              </li>
                            </Fragment>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {Boolean(message.responseToMessage.id) && (
                    <div className={cn({
                      [styles.replyTo]: true,
                      [styles.replyToDeleted]: message.responseToMessage.isDeleted,
                    })} onClick={() => this.scrollToMessage(message.responseToMessage.id, flashElement)}>
                      {!message.responseToMessage.isDeleted && (
                        <Fragment>
                          {getUserFullName(message.responseToMessage.sender)}&nbsp;
                          <span title={message.responseToMessage.text}>
                            {getMessageSnippet(message.responseToMessage)}
                          </span>
                        </Fragment>
                      )}
                      {message.responseToMessage.isDeleted && (
                        <span>Сообщение удалено</span>
                      )}
                    </div>
                  )}

                  {message.text && (
                    <span className={cn({
                      [styles.text]: true,
                      [styles.textEdited]: message.editedAt,
                    })}>
                      <FormattedMarkdown markdown={message.text} onDoubleClick={() => onReplyTo(message)}/>
                      {message.editedAt && (
                        <Tooltip dangerouslySetInnerHTML={{
                          __html: `Последнее изменение<br/> ${moment(message.editedAt).format('D MMMM YYYY,<br/> dddd, H:mm:ss')}`
                        }}>
                          <span className={styles.textEditedLabel}>(изменено)</span>
                        </Tooltip>
                      )}
                    </span>
                  )}

                  <ChatMessagesAttachments
                    attachments={message.attachments}
                    isSubmitting={message.isSubmitting}
                    onPreviewClick={attachmentId => this.fullScreenPreviewer.current.openPreview(attachmentId)}/>

                  {message.submissionError && (
                    this.renderSubmissionErrorMessage(message)
                  )}
                  {message.queued && (
                    this.renderQueuedMessage(message)
                  )}
                </div>

                {Boolean(message.linkedIssues.length) && (
                  <ul className={styles.messageIssues}>
                    {message.linkedIssues.map(issue => (
                      <li className={cn({
                        [styles.messageIssuesItem]: true,
                        [styles.messageIssuesItemClosed]: issue.isClosed,
                      })}
                        onClick={() => this.setState({ issuePreview: { issue, message } })}
                        key={issue.id}>
                        <i className={cn(styles.messageIssuesIcon, 'icon-issue')}/>
                        {issue.title}
                      </li>
                    ))}
                  </ul>
                )}

              </div>
            )}
          </Fragment>
        ))}

        <button
          type="button"
          className={cn({
            [styles.buttonScrollDown]: true,
            [styles.buttonScrollDownShow]: showScrollButton
          })}
          onClick={this.props.onScroll}>
          <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.96094 11.418C10.0391 11.418 10.1055 11.4023 10.1602 11.3711C10.2227 11.3477 10.2812 11.3086 10.3359 11.2539L19.0312 1.78516C19.1172 1.69141 19.1602 1.57812 19.1602 1.44531C19.1602 1.36719 19.1406 1.29297 19.1016 1.22266C19.0625 1.15234 19.0078 1.09375 18.9375 1.04688C18.8672 1.00781 18.7891 0.988281 18.7031 0.988281C18.5781 0.988281 18.4727 1.02734 18.3867 1.10547L9.96094 10.293L1.53516 1.10547C1.44922 1.02734 1.34375 0.988281 1.21875 0.988281C1.13281 0.988281 1.05859 1.00781 0.996094 1.04688C0.925781 1.09375 0.871094 1.15234 0.832031 1.22266C0.785156 1.29297 0.761719 1.36719 0.761719 1.44531C0.761719 1.57813 0.808594 1.69141 0.902344 1.78516L9.58594 11.2539C9.64062 11.3086 9.69531 11.3477 9.75 11.3711C9.8125 11.4023 9.88281 11.418 9.96094 11.418Z" fill="#999999"/>
          </svg>
        </button>

        {/* "Typing..." message */}
        {currentlyTypingUsers.map((user, i) => (
          <div className={cn(styles.message, styles.messageTyping)} key={i}>
            <Avatar className={styles.avatar} user={user}/>
            <div className={styles.messageContent}>
              <div className={styles.author}>
                <b>{getUserFullName(user)}</b>
                {user.isTyping && (
                  <span className={styles.typingLabel}>печатает...</span>
                )}
              </div>
            </div>

            <FormattedMarkdown
              className={styles.text}
              markdown={user.typedText}/>
          </div>
        ))}

        <FullScreenPreviews
          className={styles.fullScreenPreviews}
          previews={fullScreenPreviews}
          ref={this.fullScreenPreviewer}
          onToggle={onToggleFullScreenPreview}/>

        {linkingToIssueMessageId && (
          <IssueLinkingPopup
            messageId={linkingToIssueMessageId}
            onClose={() => this.setState({ linkingToIssueMessageId: null })}/>
        )}

        {issuePreview && (
          <Modal title="Проблема"
            verticallyCentered={true}
            onClose={() => this.setState({ issuePreview: null })}>

            <div className={styles.issuePreview}>
              <div className={styles.issuePreviewTitle}>{issuePreview?.issue?.title}</div>
              <div className={styles.issuePreviewSubtitle}>
                {inflect(issuePreview?.issue?.totalLinkedMessagesCount, ['сообщение', 'сообщения', 'сообщений'])}
                &nbsp;в {inflect(issuePreview?.issue?.totalLinkedRoomsCount, ['чате', 'чатах', 'чатах'])}
              </div>
              <Link className={uiStyles.buttonPrimary} to={'/issues/' + issuePreview?.issue?.id}>
                {isMobileSizeScreen() ? 'К проблеме' : 'Перейти к проблеме'}
              </Link>
              <button className={cn({
                [styles.issuePreviewRemoveButton]: true,
                [styles.issuePreviewRemoveButtonSubmitting]: isUnlinkingIssueFromMessage,
              })} onClick={() => this.onUnlinkIssueClick(issuePreview)}>
                Удалить связь
              </button>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}
